import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { ScrollContext } from '../contexts/ScrollContext'

interface SmoothScrollProps {
  children: ReactNode
}

type ScrollListener = (y: number) => void

const SmoothScroll = ({ children }: SmoothScrollProps) => {
  const scrollRef    = useRef<HTMLDivElement>(null)
  const listenersRef = useRef<Set<ScrollListener>>(new Set())

  const [contentHeight, setContentHeight] = useState(0)
  const [currentScroll, setCurrentScroll] = useState(0)

  // Computed once on mount. Mobile devices get a transparent pass-through so
  // native touch-scroll works correctly and layout stays in normal flow.
  const [isMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  )

  // Stable subscribe function — never changes identity, safe as a dep.
  const subscribe = useCallback((cb: ScrollListener): (() => void) => {
    listenersRef.current.add(cb)
    return () => listenersRef.current.delete(cb)
  }, [])

  // ── Mobile: track real scroll so context consumers still get scrollY ─────
  useEffect(() => {
    if (!isMobile) return

    // Gate all subscriber callbacks behind a RAF so:
    //  • Multiple scroll events per frame are deduplicated to one callback
    //  • DOM writes by subscribers land in the same browser paint cycle
    //  • Main-thread work is V-sync-aligned (matches desktop RAF behaviour)
    let rafId = 0
    const handle = () => {
      if (rafId) return // already scheduled this frame — skip
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY
        setCurrentScroll(y)
        // Notify subscribers so scroll-driven components work on mobile too.
        // On mobile there is no lerp wrapper, so we pass the raw scrollY —
        // the subscriber's CSS-sticky pin handles viewport alignment natively.
        listenersRef.current.forEach(cb => cb(y))
        rafId = 0
      })
    }

    window.addEventListener('scroll', handle, { passive: true })
    handle() // initialise
    return () => {
      window.removeEventListener('scroll', handle)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isMobile])

  // ── Desktop: smooth-scroll engine ────────────────────────────────────────
  useEffect(() => {
    if (isMobile) return

    const updateHeight = () => {
      if (scrollRef.current) setContentHeight(scrollRef.current.scrollHeight)
    }

    window.addEventListener('resize', updateHeight)

    let rafId  = 0
    let smooth = window.scrollY
    let target = window.scrollY

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t

    const tick = () => {
      target = window.scrollY
      smooth = lerp(smooth, target, 0.12)

      // 1) Move the fixed wrapper
      if (scrollRef.current) {
        scrollRef.current.style.transform = `translateY(-${smooth}px)`
      }

      // 2) Notify subscribers IN THE SAME RAF TICK so their DOM writes land
      //    in the same browser paint — this eliminates the one-frame jitter
      //    between the wrapper transform and the pinned-element compensation.
      listenersRef.current.forEach(cb => cb(smooth))

      if (Math.abs(target - smooth) > 0.5) {
        rafId = requestAnimationFrame(tick)
      } else {
        // Snap to final position
        smooth = target
        if (scrollRef.current) {
          scrollRef.current.style.transform = `translateY(-${smooth}px)`
        }
        listenersRef.current.forEach(cb => cb(smooth))

        // Only update React state ONCE when the animation ends.
        // This prevents 60 re-renders per second while scrolling.
        setCurrentScroll(smooth)
        rafId = 0
      }
    }

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(tick)
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    // Run immediately to set the initial position
    tick()

    // Height updates — catch late-rendering content
    const delays  = [0, 100, 300, 500]
    const timers  = delays.map(d => setTimeout(updateHeight, d))
    const ro      = new ResizeObserver(updateHeight)
    if (scrollRef.current) ro.observe(scrollRef.current)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', updateHeight)
      ro.disconnect()
      timers.forEach(clearTimeout)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isMobile])

  // ── Mobile: render children in normal document flow ───────────────────────
  // No position:fixed wrapper — native touch scroll works as expected.
  if (isMobile) {
    return (
      <ScrollContext.Provider value={{ scrollY: currentScroll, isSmooth: false, subscribe }}>
        {children}
      </ScrollContext.Provider>
    )
  }

  // ── Desktop: fixed wrapper driven by lerp transform ───────────────────────
  return (
    <ScrollContext.Provider value={{ scrollY: currentScroll, isSmooth: true, subscribe }}>
      {/* Spacer — gives the page its scroll height */}
      <div style={{ height: contentHeight }} />

      {/* Fixed container shifted by the smoothed scroll offset */}
      <div
        ref={scrollRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          willChange: 'transform',
        }}
      >
        {children}
      </div>
    </ScrollContext.Provider>
  )
}

export default SmoothScroll
